<template>
    <div>
        <BaseGrid
            ref="baseGrid"
            :title="$t('itemsGrid')"
            :headers="headers"
            :query-builder="queryBuilder"
            :filter-context="filterContext"
            :default-sort="[{ key: 'StatusCreatedDate', order: 'desc' }]"
            item-value="id"
        >
            <!-- Toolbar Actions -->
            <template #toolbar-actions>
                <v-btn 
                    icon 
                    variant="plain"
                    @click="showSummaryReportDialog = true"
                >
                    <v-icon>mdi-download</v-icon>
                </v-btn>
            </template>

            <!-- Desktop Row -->
            <template #desktop-row="{ item, columns }">
                <tr>
                    <td class="client-id-cell">
                        <div class="d-flex align-center">
                            <template v-if="hasSubItems(item, 1)">
                                <v-btn 
                                    :icon="expandIcon(item)"
                                    @click="toggleExpand(item)"
                                    variant="plain"
                                    class="mr-1"
                                    style="width: 24px; height: 24px;"
                                />
                            </template>

                            <v-chip label variant="text">
                                <v-icon :icon="item.categoryIcon" class="mr-2"/>
                                <a 
                                    @click.prevent="openItemUrl(item)"
                                    href="#"
                                    style="color: inherit;"
                                >
                                    {{ item.clientId }}
                                </a>
                            </v-chip>
                        </div>
                    </td>

                    <td>
                        <div class="d-flex flex-column align-start">
                            <span class="text-start">{{ item.profileTitle }}</span>
                            <span 
                                v-if="!hasSubItems(item, 1)" 
                                style="color: #888;" 
                                class="text-start text-caption"
                            >
                                {{ item.formTemplateTitle !== item.profileTitle ? item.formTemplateTitle : '' }}
                            </span>
                        </div>
                    </td>

                    <td>
                        <template v-if="!hasSubItems(item, 1)">
                            {{ item.subItems[0].assigneeTeamName }}
                        </template>
                    </td>

                    <td>
                        <AssigneeUsersCell :item="item" />
                    </td>

                    <td>
                        <LocationCell :item="item" />
                    </td>

                    <td>
                        <div class="d-flex flex-column">
                            <span v-if="item.createdByTeamName">{{ item.createdByTeamName }}</span>
                            <span v-if="item.createdByUserName" class="text-caption">{{ item.createdByUserName }}</span>
                        </div>
                    </td>

                    <td>{{ formatDate(item.updatedDate) }}</td>

                    <td>
                        <v-chip 
                            :style="{
                                backgroundColor: item.latestStatusColor,
                                color: 'white'
                            }"
                            label
                        >
                            {{ item.latestStatusTitle }}
                        </v-chip>
                    </td>
                </tr>

                <!-- Expanded Sub-items Row -->
                <tr v-if="isExpanded(item) && item.subItems.length > 1">
                    <td :colspan="columns.length">
                        <SubItemsTable :sub-items="item.subItems" />
                    </td>
                </tr>
            </template>

            <!-- Mobile Item -->
            <template #mobile-item="{ item }">
                <v-card class="mb-2" variant="flat">
                    <v-card-title class="d-flex justify-space-between align-center">
                        <v-chip label variant="text">
                            <v-icon :icon="item.categoryIcon" class="mr-2"/>
                            <a 
                                @click.prevent="openItemUrl(item)"
                                href="#"
                                style="color: inherit;"
                            >
                                {{ item.clientId }}
                            </a>
                        </v-chip>
                        <v-chip 
                            :style="{
                                backgroundColor: item.latestStatusColor,
                                color: 'white'
                            }"
                            label
                        >
                            {{ item.latestStatusTitle }}
                        </v-chip>
                    </v-card-title>
                    
                    <v-card-text>
                        <MobileItemContent :item="item" @show-sub-items="showSubItemsFor" />
                    </v-card-text>
                </v-card>
            </template>
        </BaseGrid>

        <ItemsGridPaginatedReportsDialog
            v-model:showSummaryReportDialog="showSummaryReportDialog"
        />

        <SubItemsDialog 
            v-model="showSubItemsDialog"
            :item="selectedSubItems"
        />
    </div>
</template>

<script setup>
import { ref, computed, getCurrentInstance } from 'vue';
import { useStore } from 'vuex';
import { format } from 'date-fns';
import { useI18n } from 'vue-i18n';
import BaseGrid from '@/components/grids/base/BaseGrid.vue';
import ItemsGridPaginatedReportsDialog from '@/components/dialogs/ItemsGridPaginatedReportsDialog.vue';
// Import other sub-components as needed

const props = defineProps({
    filterContext: {
        type: Object,
        required: true
    }
});

const store = useStore();
const { t } = useI18n();
const settings = computed(() => store.state.settings);
const buildMode = computed(() => store.state.buildMode);

// State
const showSummaryReportDialog = ref(false);
const showSubItemsDialog = ref(false);
const selectedSubItems = ref(null);
const expanded = ref([]);

// Headers configuration
const headers = computed(() => [
    { titleKey: 'id', key: 'clientId', align: 'start', sortable: true },
    { titleKey: 'item', key: 'profileId', align: 'start', sortable: true },
    { titleKey: 'assignedTo', key: 'AssigneeTeamId', sortable: true },
    { title: "", key: "AssigneeUserIds", sortable: true },
    { titleKey: 'location', key: 'locationId', align: 'start', sortable: true },
    { titleKey: 'createdBy', key: 'createdByTeamId', align: 'start', sortable: true },
    { titleKey: 'updatedDate', key: 'StatusCreatedDate', align: 'start', sortable: true },
    { titleKey: 'status', key: 'StatusId', align: 'start', sortable: true },
]);

// Query builder function
const queryBuilder = async ({ page, itemsPerPage, sort, search, filterContext }) => {
    // Build WHERE clause
    const whereClause = getWhereClause(search);
    
    // Build ORDER BY clause
    let orderByColumn = 'fi.id';
    let orderDirection = sort.order || 'desc';
    
    if (sort.key) {
        const columnMapping = {
            'profileId': 'it.title',
            'locationId': 'l.title',
            'StatusId': 'st.title',
            'AssigneeTeamId': 't.name',
            'createdByTeamId': 'ct.name',
            'clientId': 'fi.clientId'
        };
        
        orderByColumn = columnMapping[sort.key] || `fi.${sort.key}`;
    }
    
    const orderByClause = `ORDER BY ${orderByColumn} ${orderDirection.toUpperCase()}`;
    const limitClause = `LIMIT ${itemsPerPage}`;
    const offsetClause = `OFFSET ${(page - 1) * itemsPerPage}`;

    // Count query
    const countQuery = `
        SELECT COUNT(DISTINCT fi.id) as total
        FROM filteredItems fi
        ${whereClause.length === 0 ? '' : 
        `
            LEFT JOIN item_templates it ON fi.profileId = it.id
            LEFT JOIN categories c ON it.categoryId = c.id
            LEFT JOIN form_templates ft ON fi.SubItemTemplateId = ft.id
            LEFT JOIN status_templates st ON fi.StatusId = st.id
            LEFT JOIN teams t ON fi.AssigneeTeamId = t.id
            LEFT JOIN locations l ON fi.locationId = l.id
            LEFT JOIN drawings ld ON l.drawingId = ld.id
            LEFT JOIN teams ct ON fi.createdByTeamId = ct.id
            LEFT JOIN users u on fi.createdByUserId = u.id
        `}
        ${whereClause}
    `;

    // Get item IDs for pagination
    const idsQuery = `
        SELECT DISTINCT fi.Id as id
        FROM filteredItems fi
        ${whereClause.length === 0 ? '' : 
        `
            LEFT JOIN item_templates it ON fi.profileId = it.id
            LEFT JOIN categories c ON it.categoryId = c.id
            LEFT JOIN form_templates ft ON fi.SubItemTemplateId = ft.id
            LEFT JOIN status_templates st ON fi.StatusId = st.id
            LEFT JOIN teams t ON fi.AssigneeTeamId = t.id
            LEFT JOIN locations l ON fi.locationId = l.id
            LEFT JOIN drawings ld ON l.drawingId = ld.id
            LEFT JOIN teams ct ON fi.createdByTeamId = ct.id
            LEFT JOIN users u on fi.createdByUserId = u.id
        `}           
        ${whereClause}
        ${orderByClause}
        ${limitClause}
        ${offsetClause}
    `;

    const conn = store.getters.getDBConnection;
    const idsResult = await conn.query(idsQuery);
    const itemIds = idsResult.toArray().map(row => row.toJSON().id);

    if (itemIds.length === 0) {
        return {
            countQuery,
            itemsQuery: "SELECT 1 WHERE 1=0", // Empty result
            transformItems: () => []
        };
    }

    // Items query
    const itemsQuery = `
        SELECT
            fi.*,
            it.title AS profileTitle,
            it.categoryId,
            it.subcategoryId,
            c.icon AS categoryIcon,
            ft.title AS formTemplateTitle,
            st.title AS statusTitle,
            st.color AS statusColor,
            t.name AS assigneeTeamName,
            l.title AS locationTitle,
            l.drawingId,
            ld.title AS drawingTitle,
            ct.name AS createdByTeamName,
            cu.displayName AS createdByUserName
        FROM filteredItems fi
        LEFT JOIN item_templates it ON fi.profileId = it.id
        LEFT JOIN categories c ON it.categoryId = c.id
        LEFT JOIN form_templates ft ON fi.subItemTemplateId = ft.id
        LEFT JOIN status_templates st ON fi.statusId = st.id
        LEFT JOIN teams t ON fi.assigneeTeamId = t.id
        LEFT JOIN locations l ON fi.locationId = l.id
        LEFT JOIN drawings ld ON l.drawingId = ld.id
        LEFT JOIN teams ct ON fi.createdByTeamId = ct.id
        LEFT JOIN users cu ON fi.createdByUserId = cu.id
        WHERE fi.id IN (${itemIds.map(id => `'${id}'`).join(',')})
        ${orderByClause}
    `;

    return {
        countQuery,
        itemsQuery,
        transformItems: transformItemsData
    };
};

// Transform raw data into grouped items
const transformItemsData = (rawItems) => {
    const groupedItems = {};

    rawItems.forEach(rawData => {
        const parentId = rawData.id;
        
        if (!groupedItems[parentId]) {
            groupedItems[parentId] = {
                id: rawData.id,
                clientId: rawData.clientId,
                profileId: rawData.profileId,
                type: rawData.type,
                profileTitle: rawData.profileTitle,
                categoryId: rawData.categoryId,
                subcategoryId: rawData.subcategoryId,
                categoryIcon: getIconClass(rawData.categoryIcon),
                formTemplateTitle: rawData.formTemplateTitle || '',
                locationId: rawData.locationId,
                locationTitle: rawData.locationTitle,
                drawingId: rawData.drawingId,
                drawingTitle: rawData.drawingTitle,
                createdByTeamName: rawData.createdByTeamName,
                createdByUserName: rawData.createdByUserName,
                subItems: []
            };
        }
        
        const subitem = {
            formTemplateTitle: rawData.formTemplateTitle,
            statusTitle: rawData.statusTitle,
            statusColor: rawData.statusColor,
            statusCreatedDate: rawData.statusCreatedDate,
            assigneeTeamName: rawData.assigneeTeamName || '',
            assigneeUsers: rawData.subitem_assigneeUsers ? JSON.parse(rawData.subitem_assigneeUsers) : []
        };
        
        groupedItems[parentId].subItems.push(subitem);
    });

    let items = Object.values(groupedItems);
    
    // Add latest status info
    items = items.map(item => {
        let latestSubitem = null;
        
        if (item.subItems && item.subItems.length > 0) {
            latestSubitem = item.subItems.reduce((latest, current) => {
                const latestDate = latest.statusCreatedDate ? new Date(latest.statusCreatedDate) : new Date(0);
                const currentDate = current.statusCreatedDate ? new Date(current.statusCreatedDate) : new Date(0);
                return currentDate > latestDate ? current : latest;
            }, item.subItems[0]);
        }
        
        return {
            ...item,
            latestStatusTitle: latestSubitem ? latestSubitem.statusTitle : '',
            latestStatusColor: latestSubitem ? latestSubitem.statusColor : '',
            updatedDate: latestSubitem ? latestSubitem.statusCreatedDate : null
        };
    });
    
    return items;
};

// Helper methods
const getWhereClause = (search) => {
    if (!search?.trim()) return '';
    
    const searchTerm = search.trim().toLowerCase();
    return `
        WHERE (
            LOWER(fi.clientId) LIKE '%${searchTerm}%'
            OR LOWER(it.title) LIKE '%${searchTerm}%'
            OR LOWER(t.name) LIKE '%${searchTerm}%'
            OR LOWER(l.title) LIKE '%${searchTerm}%'
            OR LOWER(ld.title) LIKE '%${searchTerm}%'
            OR LOWER(st.title) LIKE '%${searchTerm}%'
            OR LOWER(ct.name) LIKE '%${searchTerm}%'
            OR LOWER(u.displayName) LIKE '%${searchTerm}%'
        )
    `;
};

const getIconClass = (icon) => {
    if (!icon) return 'mdi-help-circle';
    
    if (icon.startsWith('mdi-')) {
        return icon;
    } else if (icon.startsWith('fas fa-') || icon.startsWith('fab fa-')) {
        return `fa:${icon}`;
    } else if (icon.startsWith('fa-')) {
        return `fa:fas ${icon}`;
    } else {
        return `md:${icon}`;
    }
};

const formatDate = (dateString) => {
    if (!dateString) return '';
    return format(new Date(dateString), 'yyyy-MM-dd');
};

const hasSubItems = (item, minCount = 1) => {
    return item.subItems.length > minCount;
};

const toggleExpand = (item) => {
    const index = expanded.value.indexOf(item.id);
    if (index === -1) {
        expanded.value.push(item.id);
    } else {
        expanded.value.splice(index, 1);
    }
};

const isExpanded = (item) => {
    return expanded.value.includes(item.id);
};

const expandIcon = computed(() => (item) => {
    return isExpanded(item) ? 'mdi-chevron-down' : 'mdi-chevron-right';
});

const openItemUrl = (item) => {
    const baseUrl = settings.value.apiUrl.startsWith('api-') 
        ? settings.value.apiUrl.slice(4) 
        : settings.value.apiUrl;
    const path = item.type === 'IssueTemplate' ? 'issues?issue' : 'processes?process';
    const url = `https://${baseUrl}/${settings.value.projectCode}/${path}-client-ids=${item.clientId}&subcategory-ids=${item.subcategoryId}`;
    
    const { proxy } = getCurrentInstance();
    if (buildMode.value === 'production') {
        proxy.$launchUrl(url);
    } else {
        window.open(url, '_blank');
    }
};

const showSubItemsFor = (item) => {
    selectedSubItems.value = item;
    showSubItemsDialog.value = true;
};
</script>